import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { digitalApi as api } from './api';
import { useAuth } from '../../context/AuthContext';
import { Modal } from './DigitalClientsPage';
import S, { C, STATUS_COLOR, STATUS_ICON } from './styles';

export default function DigitalClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit, canApprove, isAdmin } = useAuth();

  const [client, setClient] = useState(null);
  const [plans, setPlans]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]   = useState(false);
  const [form, setForm]     = useState({ name:'', year: new Date().getFullYear() });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  async function load() {
    try {
      const [c, p] = await Promise.all([api.getClient(id), api.getPlans(id)]);
      setClient(c); setPlans(p);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [id]);

  async function createPlan() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const plan = await api.createPlan({ client_id: parseInt(id), name: form.name, year: form.year, data: {} });
      navigate(`/plans/${plan.id}`);
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  }

  async function handleAction(e, planId, action) {
    e.stopPropagation();
    if (!window.confirm(`${action} this plan?`)) return;
    try {
      if (action === 'submit')  await api.submitPlan(planId);
      if (action === 'approve') await api.approvePlan(planId);
      if (action === 'reject')  await api.rejectPlan(planId);
      if (action === 'archive') await api.archivePlan(planId);
      if (action === 'delete')  await api.deletePlan(planId);
      load();
    } catch(e) { alert(e.message); }
  }

  const filtered = filter === 'all' ? plans : plans.filter(p => p.status === filter);

  if (loading) return <div style={{ ...S.page, color:C.text3 }}>Loading…</div>;
  if (!client) return <div style={{ ...S.page, color:C.red }}>Client not found.</div>;

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:26 }}>
        <button style={S.btnGhost} onClick={()=>navigate('/clients')}>← Clients</button>
        <div>
          <h1 style={S.h1}>{client.name}</h1>
          {client.industry && <div style={{ fontSize:13, color:C.text3, marginTop:2 }}>{client.industry}</div>}
        </div>
        <div style={{ flex:1 }}/>
        {canEdit && (
          <button style={S.btnPrimary} onClick={()=>setModal(true)}>+ New Plan</button>
        )}
      </div>

      {/* Status filter */}
      <div style={{ display:'flex', gap:6, marginBottom:20, flexWrap:'wrap' }}>
        {['all','draft','review','approved','archived'].map(s=>(
          <button key={s} onClick={()=>setFilter(s)} style={{
            padding:'5px 14px', borderRadius:20, fontSize:12, fontWeight:600,
            cursor:'pointer', border:'1px solid', fontFamily:"'Inter',sans-serif",
            borderColor: filter===s ? C.gold : C.border,
            background:  filter===s ? C.goldBg : 'transparent',
            color:       filter===s ? C.goldDark : C.text3,
          }}>
            {s==='all' ? `All (${plans.length})` : `${STATUS_ICON[s]} ${s} (${plans.filter(p=>p.status===s).length})`}
          </button>
        ))}
      </div>

      {/* Plans list */}
      {filtered.length === 0 ? (
        <div style={S.empty}>
          {filter==='all' ? 'No plans yet. Create the first digital media plan for this client.' : `No ${filter} plans.`}
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {filtered.map(plan => (
            <div key={plan.id}
              style={S.planRow}
              onClick={()=>navigate(`/plans/${plan.id}`)}
              onMouseEnter={e=>e.currentTarget.style.boxShadow='0 2px 12px rgba(193,154,107,.2)'}
              onMouseLeave={e=>e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,.05)'}>
              {/* Status bar */}
              <div style={{ width:4, borderRadius:4, alignSelf:'stretch', flexShrink:0, background: STATUS_COLOR[plan.status] || C.text3 }}/>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                  <span style={{ fontWeight:700, fontSize:14, color:C.text }}>{plan.name}</span>
                  <span style={S.badge(STATUS_COLOR[plan.status] || C.text3)}>
                    {STATUS_ICON[plan.status]} {plan.status}
                  </span>
                  {plan.year && <span style={{ fontSize:11, color:C.text3 }}>{plan.year}</span>}
                </div>
                <div style={{ fontSize:11, color:C.text3, fontFamily:"'Inter',sans-serif" }}>
                  v{plan.version || 1} · Updated {new Date(plan.updated_at).toLocaleDateString()} by {plan.updated_by_name || plan.created_by_name || '—'}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display:'flex', gap:6, flexShrink:0 }} onClick={e=>e.stopPropagation()}>
                {plan.status==='draft' && canEdit && (
                  <button style={S.btnAction} onClick={e=>handleAction(e,plan.id,'submit')}>Submit for Review</button>
                )}
                {plan.status==='review' && canApprove && (<>
                  <button style={{ ...S.btnAction, color:C.green, borderColor:C.green+'44' }} onClick={e=>handleAction(e,plan.id,'approve')}>✓ Approve</button>
                  <button style={{ ...S.btnAction, color:C.red,   borderColor:C.red+'44'   }} onClick={e=>handleAction(e,plan.id,'reject')}>✗ Reject</button>
                </>)}
                {isAdmin && plan.status!=='archived' && (
                  <button style={S.btnAction} onClick={e=>handleAction(e,plan.id,'archive')}>Archive</button>
                )}
                {isAdmin && (
                  <button style={{ ...S.btnAction, color:C.red }} onClick={e=>handleAction(e,plan.id,'delete')}>Delete</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New plan modal */}
      {modal && (
        <Modal title="New Digital Plan" onClose={()=>setModal(false)}>
          <label style={{ display:'block', marginBottom:14 }}>
            <span style={S.label}>Plan Name *</span>
            <input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))}
              placeholder="e.g. Q1 2026 Digital Campaign" style={S.input} autoFocus />
          </label>
          <label style={{ display:'block', marginBottom:22 }}>
            <span style={S.label}>Year</span>
            <input type="number" value={form.year} onChange={e=>setForm(p=>({...p,year:+e.target.value}))} style={S.input} />
          </label>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={S.btnGhost} onClick={()=>setModal(false)}>Cancel</button>
            <button style={S.btnPrimary} onClick={createPlan} disabled={saving}>
              {saving ? 'Creating…' : 'Create & Open'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
